import Cookies, { type CookieAttributes } from 'js-cookie';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { sendPackageOperationalEvent } from '../../../common/utils';
import { Logger } from '../../../common/utils/logger';

/**
 * Deletes a cookie.
 *
 * @param attributes - Forwarded as-is to js-cookie's `Cookies.remove`. The `CookieAttributes` type
 *   is broader than what js-cookie actually serializes: its index signature accepts arbitrary
 *   keys/values, but js-cookie only handles `expires` (`number | Date`), string-valued attributes
 *   (`path`, `domain`, …) and boolean flags (`secure`). A non-string value under any other key
 *   makes js-cookie throw while serializing. This function does not coerce attribute values.
 *
 * @throws Behind `platform_moonjelly_bsc_throw_cookie_errors`, re-throws any error from
 *   `Cookies.remove` (e.g. an invalid attribute value) after recording a `usedDeleteCookieError`
 *   operational event, so callers passing invalid attributes fail fast. When the gate is off the
 *   error is logged and swallowed (the previous behaviour).
 */
export const deleteCookie = (key: string, attributes?: CookieAttributes): void => {
	if (fg('platform_moonjelly_bsc_master_killswitch')) {
		// Delete the cookie without package operational events
		// when master killswitch is enabled
		Cookies.remove(key, attributes);
	} else {
		try {
			sendPackageOperationalEvent({
				action: 'usedDeleteCookie',
				attributes: { cookieKey: key },
			});
			Cookies.remove(key, attributes);
		} catch (e: any) {
			Logger.errorWithOperationalEvent({
				action: 'usedDeleteCookieError',
				attributes: { cookieKey: key },
				message: `Failed to use delete cookie. ${e.message || ''}`,
			});
			if (fg('platform_moonjelly_bsc_throw_cookie_errors')) {
				throw e;
			}
		}
	}
};
